import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Shield, X, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface MessageFilteredModalProps {
	isOpen: boolean;
	onClose: () => void;
	userType: "parent" | "doula" | "admin";
}

const MessageFilteredModal: React.FC<MessageFilteredModalProps> = ({
	isOpen,
	onClose,
	userType,
}) => {
	const otherParty = userType === "parent" ? "doula" : "parent";

	return (
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
					{/* Backdrop */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="absolute inset-0 bg-black/40 backdrop-blur-sm"
						onClick={onClose}
					/>

					{/* Modal */}
					<motion.div
						initial={{ opacity: 0, scale: 0.95, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: 20 }}
						transition={{ type: "spring", duration: 0.3 }}
						className="relative w-full max-w-md mx-auto"
					>
						<Card className="glass-card border-0 shadow-2xl bg-white/95 backdrop-blur-xl">
							<CardHeader className="pb-4">
								<div className="flex items-start justify-between">
									<div className="flex items-center space-x-3">
										<div className="p-2 bg-green-100 rounded-full">
											<CheckCircle className="h-6 w-6 text-green-600" />
										</div>
										<div>
											<CardTitle className="text-lg font-bold text-gray-900 font-karla">
												Message Sent Successfully!
											</CardTitle>
											<p className="text-sm text-gray-600 font-karla">
												Content filtered for safety
											</p>
										</div>
									</div>
									<Button
										variant="ghost"
										size="sm"
										onClick={onClose}
										className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
									>
										<X className="h-4 w-4" />
									</Button>
								</div>
							</CardHeader>

							<CardContent className="space-y-4">
								{/* Success Message */}
								<div className="bg-green-50 p-4 rounded-lg border border-green-200">
									<div className="flex items-start space-x-3">
										<Filter className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
										<div>
											<p className="text-sm text-green-900 font-medium font-karla">
												Content automatically filtered
											</p>
											<p className="text-sm text-green-800 font-karla mt-1">
												Some content was automatically filtered for platform
												safety. Your {otherParty} will see the filtered version.
											</p>
										</div>
									</div>
								</div>

								{/* Platform Safety Info */}
								<div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
									<div className="flex items-start space-x-3">
										<Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
										<div>
											<p className="text-sm text-blue-900 font-medium font-karla">
												Your safety matters
											</p>
											<p className="text-sm text-blue-800 font-karla mt-1">
												We filter contact information to help protect both you
												and your {otherParty}
												while keeping communication secure within our platform.
											</p>
										</div>
									</div>
								</div>

								{/* Close Button */}
								<div className="pt-4">
									<Button
										onClick={onClose}
										className="w-full bg-primary-500 hover:bg-primary-600 text-white font-karla"
									>
										<CheckCircle className="h-4 w-4 mr-2" />
										Got it
									</Button>
								</div>
							</CardContent>
						</Card>
					</motion.div>
				</div>
			)}
		</AnimatePresence>
	);
};

export default MessageFilteredModal;
